"use client";

import { useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { Calendar, Palette, Tag, TestTube, Weight } from "lucide-react";

import { animateChildren } from "@/lib/animation-utils";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

export default function CalfBirthDetailsStep() {
  const { control, formState: { dirtyFields } } = useFormContext();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      // Set initial state for children
      Array.from(containerRef.current.children).forEach((child) => {
        (child as HTMLElement).style.opacity = "0";
        (child as HTMLElement).style.transform = "translateY(20px)";
      });

      // Animate children with staggered delay
      requestAnimationFrame(() => {
        if (containerRef.current) {
          animateChildren(
            containerRef.current,
            {
              opacity: 1,
              translateY: 0,
            },
            {
              duration: 400,
              easing: "ease-out",
            },
          );
        }
      });
    }
  }, []);

  const years = Array.from({ length: 10 }, (_, i) =>
    (new Date().getFullYear() - 5 + i).toString(),
  );
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const coatColors = [
    "Black",
    "Red",
    "White",
    "Brown",
    "Spotted",
    "Roan",
    "Brindle",
    "Gray",
    "Dun",
    "Blonde",
    "Mixed",
  ];

  return (
    <div ref={containerRef} className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-2xl font-bold text-slate-900">
          Calf Birth Details
        </h2>
        <p className="text-slate-500">
          Enter information about the calf&#39;s birth
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <FormField
          control={control}
          name="calfBirthYear"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2 flex-wrap">
                <Calendar className="h-4 w-4" />
                Birth Year
                {dirtyFields.calfBirthYear && (
                  <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                    Changed
                  </span>
                )}
              </FormLabel>
              <Select value={field.value ? String(field.value) : undefined} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select year" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="max-h-[300px] overflow-y-auto z-50 shadow-lg bg-white">
                  {years.map((year) => (
                    <SelectItem
                      key={year}
                      className={"overflow-y-auto z-50 background-white"}
                      value={String(year)}
                    >
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {dirtyFields.calfBirthYear && (
                <p className="text-[11px] text-amber-600 font-medium mt-1">
                  Data has been changed
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="calfBirthMonth"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2 flex-wrap">
                <Calendar className="h-4 w-4" />
                Birth Month
                {dirtyFields.calfBirthMonth && (
                  <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                    Changed
                  </span>
                )}
              </FormLabel>
              <Select value={field.value ? String(field.value) : undefined} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select month" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="max-h-[300px] overflow-y-auto z-50 shadow-lg bg-white ">
                  {months.map((month) => (
                    <SelectItem
                      key={month}
                      className={"overflow-y-auto z-50 background-white"}
                      value={String(month)}
                    >
                      {month}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {dirtyFields.calfBirthMonth && (
                <p className="text-[11px] text-amber-600 font-medium mt-1">
                  Data has been changed
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="calfNumber"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2 flex-wrap">
                <Tag className="h-4 w-4" />
                Calf Number
                {dirtyFields.calfNumber && (
                  <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                    Changed
                  </span>
                )}
              </FormLabel>
              <FormControl>
                <Input placeholder="Enter calf number" {...field} />
              </FormControl>
              {dirtyFields.calfNumber && (
                <p className="text-[11px] text-amber-600 font-medium mt-1">
                  Data has been changed
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="calfSex"
          render={({ field }) => (
            <FormItem className="space-y-3">
              <FormLabel className="flex items-center gap-2 flex-wrap">
                Calf Sex
                {dirtyFields.calfSex && (
                  <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                    Changed
                  </span>
                )}
              </FormLabel>
              <FormControl>
                <RadioGroup
                  className="flex gap-6"
                  value={field.value}
                  onValueChange={field.onChange}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem id="male" value="Male" />
                    <Label className="font-normal" htmlFor="male">
                      Male
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem id="female" value="Female" />
                    <Label className="font-normal" htmlFor="female">
                      Female
                    </Label>
                  </div>
                </RadioGroup>
              </FormControl>
              {dirtyFields.calfSex && (
                <p className="text-[11px] text-amber-600 font-medium mt-1">
                  Data has been changed
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="calfCoatColor"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2 flex-wrap">
                <Palette className="h-4 w-4" />
                Coat Color
                {dirtyFields.calfCoatColor && (
                  <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                    Changed
                  </span>
                )}
              </FormLabel>
              <Select value={field.value ? String(field.value) : undefined} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select coat color" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="max-h-[300px] overflow-y-auto z-50 shadow-lg bg-white ">
                  {coatColors.map((color) => (
                    <SelectItem
                      key={color}
                      className={"overflow-y-auto z-50 background-white"}
                      value={String(color)}
                    >
                      {color}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {dirtyFields.calfCoatColor && (
                <p className="text-[11px] text-amber-600 font-medium mt-1">
                  Data has been changed
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="calfBirthWeight"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2 flex-wrap">
                <Weight className="h-4 w-4" />
                Birth Weight (kg)
                {dirtyFields.calfBirthWeight && (
                  <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                    Changed
                  </span>
                )}
              </FormLabel>
              <FormControl>
                <Input
                  placeholder="Enter birth weight"
                  type="number"
                  {...field}
                />
              </FormControl>
              {dirtyFields.calfBirthWeight && (
                <p className="text-[11px] text-amber-600 font-medium mt-1">
                  Data has been changed
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="calfType"
          render={({ field }) => (
            <FormItem className="md:col-span-2">
              <FormLabel className="flex items-center gap-2 flex-wrap">
                <TestTube className="h-4 w-4" />
                Calf Type
                {dirtyFields.calfType && (
                  <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                    Changed
                  </span>
                )}
              </FormLabel>
              <FormControl>
                <RadioGroup
                  className="flex flex-wrap gap-6"
                  value={field.value}
                  onValueChange={field.onChange}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem id="natural" value="Natural" />
                    <Label className="font-normal" htmlFor="natural">
                      Natural
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem id="ai" value="AI" />
                    <Label className="font-normal" htmlFor="ai">
                      AI (Artificial Insemination)
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem id="et" value="ET" />
                    <Label className="font-normal" htmlFor="et">
                      ET (Embryo Transfer)
                    </Label>
                  </div>
                </RadioGroup>
              </FormControl>
              {dirtyFields.calfType && (
                <p className="text-[11px] text-amber-600 font-medium mt-1">
                  Data has been changed
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
